/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { ArtifactsIcon } from '@atlaskit/logo/artifacts/icon';
import { token } from '@atlaskit/tokens';
import type { LoadingComponentProps } from '@atlassian/native-embeds-core/types/ui-config';

const messages = defineMessages({
	loading: {
		id: 'native-embeds-artifacts-experience.artifacts-loading-overlay.loading',
		defaultMessage: 'Loading Artifacts',
		description: 'Accessible label while an Artifacts native embed is loading.',
	},
});

// Match Forge Gateway's LogoLoader, which remains visible inside the iframe after load.
// Compiled provides keyframes; the rest of the styling uses the ADS CSS API.
const pulse = keyframes({
	'0%': { opacity: 1 },
	'50%': { opacity: 0.4 },
	'100%': { opacity: 1 },
});

const styles = cssMap({
	container: {
		position: 'absolute',
		inset: 0,
		backgroundColor: token('elevation.surface'),
		zIndex: 1,
	},
	logo: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		width: '32px',
		height: '32px',
		// Scale the supported ADS icon size to match the gateway's 64px logo.
		transform: 'translate(-50%, -50%) scale(2)',
	},
	animated: {
		animationName: `${pulse}`,
		animationDuration: '2s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',
		'@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
	},
});

export function ArtifactsLoadingOverlay({
	animationDisabled = false,
	testId,
}: LoadingComponentProps): JSX.Element {
	const { formatMessage } = useIntl();
	return (
		<div css={styles.container} data-testid={testId} role="status">
			<div css={[styles.logo, !animationDisabled && styles.animated]}>
				<ArtifactsIcon label={formatMessage(messages.loading)} size="32" />
			</div>
		</div>
	);
}
